/**
 * EmployeePhoto — fetches the auth-gated employee photo via axios (Bearer
 * token attached), converts the blob to an object URL, and displays it in an
 * <img>. Falls back to initials placeholder when there is no photo or the
 * request fails.
 *
 * The <img> tag itself cannot send an Authorization header, which is why we
 * must fetch → blob → objectURL here rather than just setting `src`.
 */
import React, { useEffect, useState } from "react";
import { fetchEmployeePhotoBlob } from "@/lib/api";

export default function EmployeePhoto({
  employee,        // employee object; must have id + full_name; photo_url may be null
  size = 56,       // px
  className = "",
  rounded = "rounded-full",
  refreshKey = 0,  // bump to force refetch after an upload
}) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    if (!employee?.id || !employee.photo_url) {
      setSrc(null);
      return () => {};
    }
    (async () => {
      try {
        const blob = await fetchEmployeePhotoBlob(employee.id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) setSrc(null);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [employee?.id, employee?.photo_url, refreshKey]);

  const initials = (employee?.full_name || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={employee?.full_name || "Employee"}
        className={`${rounded} object-cover ${className}`}
        style={{ width: size, height: size, border: "2px solid #FFFFFF", boxShadow: "0 0 0 1px #E2E8F0" }}
        data-testid="employee-photo"
      />
    );
  }
  return (
    <span
      className={`${rounded} grid place-items-center font-display font-bold shrink-0 ${className}`}
      style={{
        width: size, height: size,
        background: "rgba(212,175,55,0.18)", color: "#7A5E12",
        fontSize: Math.max(12, Math.round(size / 3)),
      }}
      data-testid="employee-photo-placeholder"
    >
      {initials}
    </span>
  );
}
